import {
    Component,
    MeshComponent,
    Object3D,
    PhysXComponent,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {CursorTarget} from '@wonderlandengine/components';
import {InspectWorldObject} from './inspect-world-object.js';

/**
 * newspaper
 * Add it to Newspaper Object to Turn Pages
 * Gets called when an Inspect-World-Object calls it
 */
export class Newspaper extends Component {
    static TypeName = 'newspaper';
    /* Properties that are configurable in the editor */

    @property.object()
    pageCollision?: Object3D;
    @property.object()
    textPage1?: Object3D;
    @property.object()
    pagesParent?: Object3D;

    private pages?: Object3D[];
    private pageNum = 0;
    private inspectWorldObj?: Object3D;
    private player?: Object3D;
    private currentPage?: Object3D;
    private nextPage?: Object3D;

    start() {
        /* Validate Properties */
        if (!this.pageCollision) {
            throw new Error(`newspaper(${this.object.name}):pageCollision is not defined`);
        }
        if (!this.textPage1) {
            throw new Error(`newspaper(${this.object.name}):textPage1 is not defined`);
        }
        if (!this.pagesParent) {
            throw new Error(`newspaper(${this.object.name}):pagesParent is not defined`);
        }

        /* Add Cursor Functions */
        const targetForward =
            this.pageCollision.getComponent(CursorTarget) ||
            this.pageCollision.addComponent(CursorTarget)!;

        targetForward.onDown.add(this.pageOnDown.bind(this));

        /* Store pages into array */
        this.pages = [];
        for (const pages of this.pagesParent.children) {
            this.pages.push(pages);
        }

        /* Disable All Pages children's texts except the 1st Page */
        for (let i = 1; i < this.pages.length; i++) {
            const page = this.pages[i];

            /* Iterate over children of the Pages and disable their texts */
            for (const child of page.children) {
                const childTextComponent = child.getComponent(TextComponent);
                if (!childTextComponent) {
                    throw new Error(
                        `newspaper(${this.object.name}):TextComponent is missing on children of page.`
                    );
                }
                childTextComponent.active = false;
            }
        }

        /* General */
        this.pageNum = 0;
    }

    /**
     * Sets the `inspectWorldObj` property and retrieves the `player` property
     * from its `InspectWorldObject` component.
     * @param inspectWorldObj - The `Object3D` to inspect.
     * @throws An error if the `InspectWorldObject` component is missing on `inspectWorldObj`.
     */
    info(inspectWorldObj: Object3D) {
        this.inspectWorldObj = inspectWorldObj;
        const inspectWorldObjectComponent =
            inspectWorldObj.getComponent(InspectWorldObject);

        if (!inspectWorldObjectComponent) {
            throw new Error(
                `newspaper(${this.object.name}):InspectWorldObject component is missing on inspectWorldObj.`
            );
        }

        this.player = inspectWorldObjectComponent.player;
    }

    pageOnDown() {
        /* Return if exceeds page number */
        if (this.pageNum >= this.pages!.length) return;

        /* Hide texts of the current page */
        this.currentPage = this.pages![this.pageNum];
        for (const text of this.currentPage.children) {
            // TextComponent already validated in start()
            text.getComponent(TextComponent)!.active = false;
        }

        /* Increment active page index */
        this.pageNum++;

        /* Check if pageNum exceeds the number of Pages */
        if (this.pageNum >= this.pages!.length) {
            /* Reset */
            this.resetSelf();
        } else {
            /* Show texts of the next Page */
            this.nextPage = this.pages![this.pageNum];
            for (const text of this.nextPage.children) {
                text.getComponent(TextComponent)!.active = true;
            }
        }
    }

    resetSelf() {
        /* Reset Pages */
        this.pageNum = 0;
        for (const text of this.pages![0].children) {
            text.getComponent(TextComponent)!.active = true;
        }

        if (!this.inspectWorldObj) {
            console.error(`newspaper(${this.object.name}):inspectWorldObj is not defined`);
            return this;
        }

        /* Reset InspectWorldObj */
        const inspectWorldComponent = this.inspectWorldObj.getComponent(InspectWorldObject);
        if (!inspectWorldComponent) {
            console.error(
                `newspaper(${this.object.name}):InspectWorldObject component is missing on inspectWorldObj.`
            );
            return this;
        }
        inspectWorldComponent.onDown().catch(console.error);

        /* Disable Mesh & Collision of inspecteWorldObj */
        //await delay(0.1); /* Fix a bug where the mesh wasn't getting deactive */
        const inspectWorldObjectMesh = this.inspectWorldObj.getComponent(MeshComponent);
        if (inspectWorldObjectMesh) {
            inspectWorldObjectMesh.active = false;
        }
        if (this.inspectWorldObj.children) {
            for (const child of this.inspectWorldObj.children) {
                const inspectWorldObjectChildMesh = child.getComponent(MeshComponent);
                if (!inspectWorldObjectChildMesh) {
                    console.error(
                        `newspaper(${this.object.name}):Mesh componen is missing on child of inspect world object`
                    );
                } else {
                    inspectWorldObjectChildMesh.active = false;
                }
            }
        }
        const inspectWorldObjectPhysx = this.inspectWorldObj.getComponent(PhysXComponent);
        if (!inspectWorldObjectPhysx) {
            console.error(
                `newspaper(${this.object.name}):PhysXComponent is missing on inspect world object`
            );
        } else {
            inspectWorldObjectPhysx.active = false;
        }
        return this;
    }
}
